/**
 * Veris — Zertivo's internal digital currency.
 *
 * - 1 EUR  = 10 Veris
 * - 1 Veris = 0.10 EUR
 * - Symbol: Ⅴ (roman numeral 5 glyph — premium, distinctive, easy to render)
 * - Used for: wallet top-ups, plan payments, micro-transactions, future marketplace items.
 */

export const VERIS_PER_EUR = 10;
export const EUR_PER_VERIS = 1 / VERIS_PER_EUR;
export const VERIS_SYMBOL = 'Ⅴ';
export const MIN_TOPUP_EUR = 0.5;
export const MAX_TOPUP_EUR = 5000;

export const CURRENCY_MODE_KEY = 'zertivo-currency-mode';

/**
 * Read the user's preferred display currency. Falls back to 'EUR'.
 * @returns {'EUR' | 'VERIS'}
 */
export function readCurrencyMode() {
  try {
    const v = localStorage.getItem(CURRENCY_MODE_KEY);
    return v === 'VERIS' ? 'VERIS' : 'EUR';
  } catch {
    return 'EUR';
  }
}

export function writeCurrencyMode(mode) {
  try {
    localStorage.setItem(CURRENCY_MODE_KEY, mode === 'VERIS' ? 'VERIS' : 'EUR');
    // fire event so other tabs/components refresh
    window.dispatchEvent(new CustomEvent('zertivo-currency-change', { detail: { mode } }));
  } catch { /* ignore */ }
}

export function eurToVeris(eur) {
  return Math.round((Number(eur) || 0) * VERIS_PER_EUR * 100) / 100;
}

export function verisToEur(v) {
  return Math.round((Number(v) || 0) / VERIS_PER_EUR * 100) / 100;
}

const EUR_FMT = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const V_FMT   = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Format a numeric EUR amount in the given mode. */
export function formatAmount(eurAmount, mode = 'EUR') {
  if (eurAmount == null || Number.isNaN(Number(eurAmount))) return '–';
  if (mode === 'VERIS') {
    return `${V_FMT.format(eurToVeris(eurAmount))} ${VERIS_SYMBOL}`;
  }
  return EUR_FMT.format(eurAmount);
}

/** "€50.00 · 500 Ⅴ" — always shows both, best for balance cards. */
export function formatDual(eurAmount) {
  if (eurAmount == null || Number.isNaN(Number(eurAmount))) return '–';
  return `${EUR_FMT.format(eurAmount)} · ${V_FMT.format(eurToVeris(eurAmount))} ${VERIS_SYMBOL}`;
}

/** Signed formatter: keeps sign on both values. */
export function formatAmountSigned(eurAmount, mode = 'EUR') {
  const v = Number(eurAmount) || 0;
  const prefix = v > 0 ? '+' : '';
  return `${prefix}${formatAmount(v, mode)}`;
}
